// @ts-check
// Aba E-mail de Campanhas > Modelos (spec/email.md seção 19.1; contrato 09 de e-mail, seção 10): busca, filtro de
// categoria, "Arquivados", "Identidade e envio", "Novo modelo" e a grade de cartões (miniatura, nome, categoria, chips
// "Serviço" e "Com erro", uso em fluxos ativos, quem alterou e quando), com o menu Editar, Duplicar, Enviar teste,
// Arquivar ou Desarquivar e Excluir. Filtros na URL, ao lado da aba.
import { html } from 'htm/preact';
import { useEffect, useRef, useState } from 'preact/hooks';
import { navigate, searchParamsState, updateSearchParams } from '../../app/router.js';
import {
  deleteEmailTemplate, duplicateEmailTemplate, listEmailTemplates, setEmailTemplateArchived,
} from '../../data/email-templates.js';
import { ApiError } from '../../data/api-client.js';
import { loadWorkspaceMembers, workspaceMembersState } from '../../data/workspace-members.js';
import { Button, LightIconButton } from '../../ui/button.js';
import { Dropdown, DropdownMenuItems, MenuItem } from '../../ui/dropdown.js';
import { EmptyState, Skeleton, Toggle } from '../../ui/feedback.js';
import { Icon } from '../../ui/icon.js';
import { Modal } from '../../ui/modal.js';
import { useDebouncedValue } from '../../ui/hooks.js';
import { showErrorSnackbar, showSuccessSnackbar } from '../../ui/snackbar.js';
import { formatDateTime } from '../../lib/format.js';
import { formatFullName } from '../../lib/field-metadata.js';
import { EMAIL_TEMPLATE_CATEGORIES } from '../../../../shared/email-design/template-categories.js';
import { EmailTemplateThumbnail } from '../email-design/email-thumbnail.js';
import { CreateEmailTemplateDialog } from '../email-design/template-create-dialog.js';
import { EmailTemplateTestDialog } from '../email-design/template-test-dialog.js';
import { EmailSettingsDialog } from './email-settings-dialog.js';
import { t } from '../../i18n/index.js';

/** @typedef {import('../../data/email-templates.js').EmailTemplateSummary} EmailTemplateSummary */

/** Caminho do editor de um modelo. @param {string} templateId */
export const emailTemplatePath = (templateId) => `/campaigns/templates/email/${encodeURIComponent(templateId)}`;

/** @param {string | null | undefined} workspaceMemberId */
function memberName(workspaceMemberId) {
  if (!workspaceMemberId) return '';
  const member = (workspaceMembersState.value ?? []).find((item) => item.id === workspaceMemberId);
  return member ? formatFullName(member.name) || member.userEmail || '' : '';
}

/**
 * Menu de três pontos do cartão.
 * @param {{ template: EmailTemplateSummary, onAction: (action: 'EDIT' | 'DUPLICATE' | 'TEST' | 'ARCHIVE' | 'DELETE') => void }} props
 */
function TemplateCardMenu({ template, onAction }) {
  const anchorRef = useRef(/** @type {HTMLElement | null} */ (null));
  const [isOpen, setIsOpen] = useState(false);
  /** @param {'EDIT' | 'DUPLICATE' | 'TEST' | 'ARCHIVE' | 'DELETE'} action */
  const choose = (action) => {
    setIsOpen(false);
    onAction(action);
  };
  return html`<span ref=${anchorRef} class="email-card-menu" onClick=${(/** @type {MouseEvent} */ event) => event.stopPropagation()}>
    <${LightIconButton} icon="IconDotsVertical" ariaLabel=${t('emailTemplates.actions.menu', { name: template.name })} onClick=${() => setIsOpen((open) => !open)} />
    <${Dropdown} anchorRef=${anchorRef} isOpen=${isOpen} onClose=${() => setIsOpen(false)} placement="bottom-end" width=${200} role="menu">
      <${DropdownMenuItems}>
        <${MenuItem} icon="IconPencil" text=${t('emailTemplates.actions.edit')} onClick=${() => choose('EDIT')} />
        <${MenuItem} icon="IconCopy" text=${t('emailTemplates.actions.duplicate')} onClick=${() => choose('DUPLICATE')} />
        <${MenuItem} icon="IconSend" text=${t('emailTemplates.actions.sendTest')} onClick=${() => choose('TEST')} />
        <${MenuItem} icon=${template.archivedAt ? 'IconRestore' : 'IconArchive'} text=${template.archivedAt ? t('emailTemplates.actions.unarchive') : t('emailTemplates.actions.archive')}
          onClick=${() => choose('ARCHIVE')} />
        <${MenuItem} icon="IconTrash" accent="danger" text=${t('emailTemplates.actions.delete')} onClick=${() => choose('DELETE')} />
      <//>
    <//>
  </span>`;
}

/**
 * @param {{ template: EmailTemplateSummary, onAction: (action: 'EDIT' | 'DUPLICATE' | 'TEST' | 'ARCHIVE' | 'DELETE') => void }} props
 */
function TemplateCard({ template, onAction }) {
  const updatedBy = memberName(template.updatedByWorkspaceMemberId);
  return html`<li class="email-template-card" data-archived=${template.archivedAt ? 'true' : undefined}>
    <a class="email-template-card-link" href=${emailTemplatePath(template.id)}
      onClick=${(/** @type {MouseEvent} */ event) => {
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
        event.preventDefault();
        onAction('EDIT');
      }}>
      <${EmailTemplateThumbnail} templateId=${template.id} updatedAt=${template.updatedAt} title=${template.name} />
      <span class="email-template-card-name">${template.name}</span>
    </a>
    <div class="email-template-card-meta">
      <span class="email-chip-row">
        <span class="email-chip">${t(`emailTemplates.categories.${template.category}`)}</span>
        <span class="email-chip" data-tone="gray">${t(`emailTemplates.modes.${template.editorMode}`)}</span>
        ${template.purpose === 'SERVICE' ? html`<span class="email-chip" data-tone="blue">${t('emailTemplates.card.service')}</span>` : null}
        ${template.hasErrors ? html`<span class="email-chip" data-tone="red"><${Icon} name="IconAlertTriangle" size=${12} />${t('emailTemplates.card.hasErrors')}</span>` : null}
        ${template.archivedAt ? html`<span class="email-chip" data-tone="gray">${t('emailTemplates.card.archived')}</span>` : null}
      </span>
      ${template.usage?.liveFlows ? html`<span class="email-template-card-usage">${t('emailTemplates.card.usedInFlows', { count: template.usage.liveFlows })}</span>` : null}
      <span class="email-template-card-updated">
        ${updatedBy ? t('emailTemplates.card.updatedBy', { date: formatDateTime(template.updatedAt), name: updatedBy }) : t('emailTemplates.card.updatedAt', { date: formatDateTime(template.updatedAt) })}
      </span>
    </div>
    <${TemplateCardMenu} template=${template} onAction=${onAction} />
  </li>`;
}

export function EmailTemplatesTab() {
  const parameters = searchParamsState.value;
  const category = parameters.get('category') ?? '';
  const showArchived = parameters.get('archived') === '1';
  const [search, setSearch] = useState(parameters.get('search') ?? '');
  const debouncedSearch = useDebouncedValue(search.trim(), 300);
  const [templates, setTemplates] = useState(/** @type {EmailTemplateSummary[] | null} */ (null));
  const [loadFailed, setLoadFailed] = useState(false);
  const [creation, setCreation] = useState(/** @type {'CHOOSE' | 'GALLERY' | null} */ (null));
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [testTarget, setTestTarget] = useState(/** @type {EmailTemplateSummary | null} */ (null));
  const [pendingDelete, setPendingDelete] = useState(/** @type {EmailTemplateSummary | null} */ (null));
  const [blockingFlows, setBlockingFlows] = useState(/** @type {Array<{ id: string | null, name: string | null, isLive: boolean }> | null} */ (null));
  const [isWorking, setIsWorking] = useState(false);

  const load = () => {
    setLoadFailed(false);
    listEmailTemplates({ search: debouncedSearch || undefined, category: category || undefined, archived: showArchived })
      .then(setTemplates)
      .catch(() => setLoadFailed(true));
  };
  useEffect(() => {
    loadWorkspaceMembers();
  }, []);
  useEffect(load, [debouncedSearch, category, showArchived]);
  useEffect(() => {
    if ((parameters.get('search') ?? '') !== debouncedSearch) updateSearchParams({ search: debouncedSearch || null });
  }, [debouncedSearch]);

  /** @param {EmailTemplateSummary} template @param {'EDIT' | 'DUPLICATE' | 'TEST' | 'ARCHIVE' | 'DELETE'} action */
  const handleAction = async (template, action) => {
    if (action === 'EDIT') {
      navigate(emailTemplatePath(template.id));
      return;
    }
    if (action === 'TEST') {
      setTestTarget(template);
      return;
    }
    if (action === 'DELETE') {
      setPendingDelete(template);
      return;
    }
    try {
      if (action === 'DUPLICATE') {
        const copy = await duplicateEmailTemplate(template.id);
        showSuccessSnackbar(t('emailTemplates.duplicated', { name: copy.name }));
      } else {
        await setEmailTemplateArchived(template.id, !template.archivedAt);
        showSuccessSnackbar(template.archivedAt ? t('emailTemplates.unarchived') : t('emailTemplates.archivedDone'));
      }
      load();
    } catch (error) {
      showErrorSnackbar(error);
    }
  };

  const confirmDelete = async () => {
    if (!pendingDelete) return;
    setIsWorking(true);
    try {
      await deleteEmailTemplate(pendingDelete.id);
      showSuccessSnackbar(t('emailTemplates.deleted'));
      setPendingDelete(null);
      load();
    } catch (error) {
      setPendingDelete(null);
      if (error instanceof ApiError && error.code === 'EMAIL_TEMPLATE_IN_USE') setBlockingFlows(Array.isArray(error.details?.usages) ? error.details.usages : []);
      else showErrorSnackbar(error);
    } finally {
      setIsWorking(false);
    }
  };

  const isFiltered = Boolean(debouncedSearch || category || showArchived);
  return html`<div class="email-templates-tab" data-testid="email-templates-tab">
    <div class="email-templates-toolbar">
      <label class="email-search-field">
        <${Icon} name="IconSearch" size=${14} />
        <input type="search" value=${search} placeholder=${t('emailTemplates.search')} aria-label=${t('emailTemplates.search')}
          onInput=${(/** @type {Event} */ event) => setSearch(/** @type {HTMLInputElement} */ (event.currentTarget).value)} />
      </label>
      <select class="text-input email-select email-category-filter" value=${category} aria-label=${t('emailTemplates.categoryFilter')}
        onChange=${(/** @type {Event} */ event) => updateSearchParams({ category: /** @type {HTMLSelectElement} */ (event.currentTarget).value || null })}>
        <option value="">${t('emailTemplates.allCategories')}</option>
        ${EMAIL_TEMPLATE_CATEGORIES.map((key) => html`<option value=${key}>${t(`emailTemplates.categories.${key}`)}</option>`)}
      </select>
      <label class="email-inline-toggle">
        <${Toggle} size="small" value=${showArchived} ariaLabel=${t('emailTemplates.archived')}
          onChange=${(/** @type {boolean} */ value) => updateSearchParams({ archived: value ? '1' : null })} />
        <span>${t('emailTemplates.archived')}</span>
      </label>
      <span class="email-toolbar-spacer"></span>
      <${Button} title=${t('emailTemplates.identityAndSending')} icon="IconPalette" variant="secondary" size="small" onClick=${() => setIsSettingsOpen(true)} />
      <${Button} title=${t('emailTemplates.newTemplate')} icon="IconPlus" accent="blue" size="small" onClick=${() => setCreation('CHOOSE')} data-testid="email-new-template" />
    </div>
    ${loadFailed
      ? html`<div class="email-state">
          <${EmptyState} icon="IconAlertTriangle" title=${t('emailTemplates.loadError')}
            action=${html`<${Button} title=${t('emailTemplates.retry')} variant="secondary" size="small" onClick=${load} />`} />
        </div>`
      : templates === null
        ? html`<ul class="email-template-grid" aria-busy="true">${Array.from({ length: 6 }, (_, index) => html`<li key=${index} class="email-template-card email-template-card-skeleton">
            <${Skeleton} height=${266} radius=${6} /><${Skeleton} width="70%" height=${14} /><${Skeleton} width="40%" height=${12} />
          </li>`)}</ul>`
        : templates.length === 0
          ? html`<div class="email-state">
              ${debouncedSearch
                ? html`<${EmptyState} icon="IconSearch" title=${t('emailTemplates.noResults', { search: debouncedSearch })} />`
                : isFiltered
                  ? html`<${EmptyState} icon="IconFilter" title=${t('emailTemplates.noFilteredResults')} />`
                  : html`<${EmptyState} icon="IconMail" title=${t('emailTemplates.emptyTitle')} subtitle=${t('emailTemplates.emptyDescription')}
                      action=${html`<${Button} title=${t('emailTemplates.emptyAction')} icon="IconLayoutGrid" accent="blue" size="small" onClick=${() => setCreation('GALLERY')} />`} />`}
            </div>`
          : html`<ul class="email-template-grid">
              ${templates.map((template) => html`<${TemplateCard} key=${template.id} template=${template}
                onAction=${(/** @type {'EDIT' | 'DUPLICATE' | 'TEST' | 'ARCHIVE' | 'DELETE'} */ action) => handleAction(template, action)} />`)}
            </ul>`}
    ${creation
      ? html`<${CreateEmailTemplateDialog} initialStep=${creation} onClose=${() => setCreation(null)}
          onCreated=${(/** @type {{ id: string }} */ template) => {
            setCreation(null);
            navigate(emailTemplatePath(template.id));
          }} />`
      : null}
    ${isSettingsOpen ? html`<${EmailSettingsDialog} onClose=${() => setIsSettingsOpen(false)} />` : null}
    ${testTarget
      ? html`<${EmailTemplateTestDialog} templateId=${testTarget.id} templateName=${testTarget.name} hasErrors=${testTarget.hasErrors} onClose=${() => setTestTarget(null)} />`
      : null}
    ${pendingDelete
      ? html`<${Modal} title=${t('emailTemplates.confirmDelete.title')} subtitle=${t('emailTemplates.confirmDelete.description', { name: pendingDelete.name })}
          confirmLabel=${t('emailTemplates.actions.delete')} confirmAccent="danger" isLoading=${isWorking} onClose=${() => setPendingDelete(null)} onConfirm=${confirmDelete} />`
      : null}
    ${blockingFlows
      ? html`<${Modal} title=${t('emailTemplates.confirmDelete.inUseTitle')} subtitle=${t('emailTemplates.confirmDelete.inUse')} confirmLabel=${t('common.close')} hideCancel=${true}
          onClose=${() => setBlockingFlows(null)} onConfirm=${() => setBlockingFlows(null)}>
          <ul class="email-usage-list">
            ${blockingFlows.map((flow) => html`<li>${flow.name ?? t('emailTemplates.confirmDelete.hiddenFlow')}${flow.isLive ? html` <span class="email-chip" data-tone="green">${t('emailTemplates.confirmDelete.live')}</span>` : null}</li>`)}
          </ul>
        <//>`
      : null}
  </div>`;
}
